import { useQueryLoader } from 'react-relay';

import logger from 'src/util/logger';

import getRouteUuidRetryVariables from './getRouteUuidRetryVariables';

/**
 * Shared retry plumbing for UUID-keyed Relay route renderers
 * (e.g. `TradeListRoute`, `PropertyShowRoute`).
 * Wraps `useQueryLoader` so a route can switch from its initial found-relay
 * result to a freshly fetched query reference when the user retries after an
 * error, while guarding against a missing route `uuid`.
 * @param {object} query - The Relay query to load on retry.
 * @param {object} routeProps - Found-relay route props (match/router/location).
 * @param {object} options - Hook options.
 * @param {string} options.logLabel - Label used when logging a skipped retry.
 * @returns {{queryRef: object, handleRetry: Function, retryDisabled: boolean}}
 *   The current query reference, the retry handler, and whether retry is disabled.
 */
export default function useRouteQueryRetry(query, routeProps, { logLabel }) {
  const [queryRef, loadQuery] = useQueryLoader(query, null);
  const retryVariables = getRouteUuidRetryVariables(routeProps);
  const retryDisabled = !retryVariables?.uuid;

  const handleRetry = () => {
    if (!retryVariables?.uuid) {
      logger.error(`${logLabel} retry skipped because the required uuid is missing.`);
      return;
    }

    loadQuery(retryVariables, {
      fetchPolicy: 'network-only',
    });
  };

  return { queryRef, handleRetry, retryDisabled };
}
